import type { WorldMapPoi } from './worldmap-api';

/**
 * Pure(ish) canvas math and drawing helpers, shared by two screens that each
 * use their OWN projection:
 *
 * - colony-city-renderer.ts's 5x5 build grid still uses the ORIGINAL
 *   trapezoid projection (gridToProjected/rowWidthRatio/drawGridLines/
 *   FAR_EDGE_WIDTH_RATIO below) — tilted, no rotation (grid columns stay
 *   vertical), far edge narrower than near edge, simulating a camera looking
 *   down at an angle. Every cell's on-screen size varies by row.
 * - worldmap-screen.ts (the world map) uses the SHEARED ISOMETRIC projection
 *   further down this file (isoGridToProjected and everything prefixed
 *   `iso`/`Iso`) — every ROW stays a straight horizontal line (flat top edge,
 *   flat bottom edge), and each COLUMN is a straight line sheared sideways as
 *   it descends, so the map's overall silhouette is a flat-topped
 *   parallelogram rather than a single pointed diamond corner. Every
 *   individual tile is itself a smaller parallelogram (flat top/bottom,
 *   slanted sides) of identical size and shape regardless of position — an
 *   earlier version of this file rotated the whole grid into a true 4-pointed
 *   diamond lattice instead (classic SimCity/Civilization/Age of Empires
 *   look), but a map with pointed corners read as "running corner to corner"
 *   rather than as a rectangle made of sectors, so it was replaced with this
 *   pure-shear scheme instead.
 *
 * These are two genuinely different transforms kept side by side rather
 * than one generalized to cover both — unifying them would mean threading
 * an extra mode flag through every caller for a resemblance that's only
 * skin-deep (both are affine, but with different basis vectors).
 */

/**
 * The world map's grid is NOT square — it mirrors a real planet's
 * equirectangular projection, the standard 2:1 width:height aspect ratio for
 * a wrapped sphere's surface map (Mercator/equirectangular world maps use
 * the same ratio). Backend: backend/src/game/worldmap/worldmap.service.ts.
 */
export const GRID_WIDTH = 80;
export const GRID_HEIGHT = 40;

export interface PanOffset {
  x: number;
  y: number;
}

export interface GridCell {
  x: number;
  y: number;
}

/**
 * Clamp a pan offset so displayed content never scrolls past its own edges.
 * If the content is smaller than the viewport on an axis, that axis is
 * centered instead of allowing free panning (there is nothing to pan to).
 * Generic — used both for the main view (content = the Mars image at its
 * current on-screen size) and, historically, flat grids.
 */
export function clampPanOffset(
  offset: PanOffset,
  viewportWidth: number,
  viewportHeight: number,
  contentWidth: number,
  contentHeight: number = contentWidth,
): PanOffset {
  const clampAxis = (value: number, viewport: number, contentSize: number): number => {
    if (contentSize <= viewport) {
      return (viewport - contentSize) / 2;
    }
    const min = viewport - contentSize;
    return Math.min(0, Math.max(min, value));
  };
  return {
    x: clampAxis(offset.x, viewportWidth, contentWidth),
    y: clampAxis(offset.y, viewportHeight, contentHeight),
  };
}

/**
 * baseScale (worldmap-screen.ts resize()) sizes the map so DEFAULT_ZOOM
 * shows this fraction of the map's width on screen. MIN_ZOOM/MAX_ZOOM are
 * derived from it so zooming out/in land on specific target fractions:
 * visibleWidthFraction(zoom) = DEFAULT_VISIBLE_WIDTH_FRACTION / zoom.
 */
export const DEFAULT_VISIBLE_WIDTH_FRACTION = 1 / 6;
/** 1 / (6 * MIN_ZOOM) = 1/4 visible at max zoom-out. */
export const MIN_ZOOM = 2 / 3;
/** 1 / (6 * MAX_ZOOM) ≈ 1/10.5 visible at max zoom-in — 5% further in than the original 5/3 (1/10). */
export const MAX_ZOOM = (5 / 3) * 1.05;
export const DEFAULT_ZOOM = 1;

/**
 * `maxZoom` defaults to the constant MAX_ZOOM but can be overridden — the
 * flat, viewport-fit projection means a grid cell's on-screen size is
 * `wrapWidth / GRID_WIDTH * zoom`, so a fixed zoom ceiling gives a much
 * smaller tap target on a narrow viewport than a wide one. worldmap-screen.ts
 * derives a per-viewport ceiling (see MIN_TAP_TARGET_PX) so cells stay
 * tappable on mobile; everything else keeps using the MAX_ZOOM default.
 */
export function clampZoom(zoom: number, maxZoom: number = MAX_ZOOM): number {
  return Math.min(maxZoom, Math.max(MIN_ZOOM, zoom));
}

/**
 * Recompute the pan offset so that whatever point currently projects to
 * `anchor` (in canvas-space) keeps projecting there after the zoom changes —
 * the standard "zoom toward cursor/pinch-center" feel. Callers should clamp
 * the result with `clampPanOffset` afterward. Scale-agnostic: works for any
 * content whose on-screen size scales linearly with `zoom`.
 */
export function computeZoomedPan(oldZoom: number, newZoom: number, oldPan: PanOffset, anchor: PanOffset): PanOffset {
  const ratio = newZoom / oldZoom;
  return {
    x: anchor.x - (anchor.x - oldPan.x) * ratio,
    y: anchor.y - (anchor.y - oldPan.y) * ratio,
  };
}

/** A minimap-space rectangle — see worldmap-screen.ts's computeMinimapRect for how it's derived under the trapezoid projection. */
export interface MinimapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// ---------------------------------------------------------------------------
// Trapezoid grid projection (the tilt)
// ---------------------------------------------------------------------------

/**
 * How much narrower the far/north edge is than the near/south edge, as a
 * fraction (1 = no convergence at all, i.e. a flat rectangle). This is the
 * one knob that makes the map read as tilted rather than merely squashed —
 * a uniform Y scale alone is visually indistinguishable from a top-down view
 * rendered shorter. Rows stay evenly spaced top-to-bottom (see
 * gridToProjected's `yu = t`, deliberately linear) so the inverse projection
 * stays closed-form instead of needing iteration.
 */
export const FAR_EDGE_WIDTH_RATIO = 0.45;

/** Row width as a fraction of the near edge's full width, at fractional row position t in [0, 1] (0 = far/north edge, 1 = near/south edge). */
export function rowWidthRatio(t: number, farWidthRatio: number = FAR_EDGE_WIDTH_RATIO): number {
  return farWidthRatio + (1 - farWidthRatio) * t;
}

export interface ProjectedPoint {
  x: number;
  y: number;
}

/**
 * Grid cell -> unit-space trapezoid position. `x` is centered on 0, ranging
 * over `[-0.5 * rowWidth(t), 0.5 * rowWidth(t)]` (narrower toward the far
 * edge); `y` is exactly `gy / gridHeight` (0 at the far edge, 1 at the near
 * edge — linear, not itself compressed). Callers scale this unit square to
 * actual screen pixels via a display width/height and pan offset — see
 * worldmap-screen.ts's toScreen().
 */
export function gridToProjected(
  gx: number,
  gy: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  farWidthRatio: number = FAR_EDGE_WIDTH_RATIO,
): ProjectedPoint {
  const t = gy / gridHeight;
  return { x: (gx / gridWidth - 0.5) * rowWidthRatio(t, farWidthRatio), y: t };
}

/** Inverse of gridToProjected, unrounded — the fractional grid position under a given unit-space point. */
export function projectedToGridContinuous(
  xu: number,
  yu: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  farWidthRatio: number = FAR_EDGE_WIDTH_RATIO,
): ProjectedPoint {
  const t = yu;
  const width = rowWidthRatio(t, farWidthRatio);
  const gx = width > 0 ? (xu / width + 0.5) * gridWidth : 0;
  return { x: gx, y: t * gridHeight };
}

/**
 * Unit-space point -> the grid cell containing it, or null if it falls
 * outside the grid (including anywhere in the polar margins beyond y in
 * [0, 1] — same "visible ground, not a selectable plot" behavior as before
 * the tilt).
 *
 * Uses `Math.floor`, not round: integer grid coordinates are cell *edges*
 * under this projection (gridToProjected(k, ...) is the boundary between
 * cell k-1 and cell k; a cell's own center is at k+0.5 — see drawGridLines
 * drawing edges at integer k, and worldmap-screen.ts's POI markers at
 * `poi.x + 0.5`). Flooring the continuous position buckets it into whichever
 * `[k, k+1)` span it falls in — rounding would push anything past a cell's
 * exact center into the next cell over.
 */
export function projectedToGridCell(
  xu: number,
  yu: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  farWidthRatio: number = FAR_EDGE_WIDTH_RATIO,
): GridCell | null {
  const exact = projectedToGridContinuous(xu, yu, gridWidth, gridHeight, farWidthRatio);
  // `|| 0` normalizes a `-0` result to `0` — behaviorally identical but
  // avoids surprising -0/0 mismatches for callers doing deep-equality checks.
  const gx = Math.floor(exact.x) || 0;
  const gy = Math.floor(exact.y) || 0;
  if (gx < 0 || gx >= gridWidth || gy < 0 || gy >= gridHeight) return null;
  return { x: gx, y: gy };
}

export interface ThemeColors {
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  text: string;
  textDim: string;
  gold: string;
  goldBright: string;
  crimson: string;
  purple: string;
  purpleLight: string;
  error: string;
  success: string;
}

/**
 * Fallback hex values mirror styles/app.css `@theme` tokens at the time of
 * writing. They only kick in if the live custom property resolves to an
 * empty string — jsdom (no stylesheet loaded) in tests, or a draw call that
 * races CSS application in a real browser. Canvas silently no-ops on
 * `fillStyle = ''` (keeps the previous color), so an unguarded read would
 * fail invisibly instead of loudly.
 */
const THEME_FALLBACKS: ThemeColors = {
  bg: '#150c08',
  surface: '#241708',
  surface2: '#1e1209',
  border: '#4a2c1a',
  text: '#f3e4d0',
  textDim: '#a8815c',
  gold: '#e2601f',
  goldBright: '#f5763a',
  crimson: '#e6342c',
  purple: '#7b93b0',
  purpleLight: '#9db2c9',
  error: '#ef4444',
  success: '#3ecf8e',
};

/** Resolve the app's design tokens as canvas-usable color strings, never hardcoded per call site. */
export function readThemeColors(doc: Document = document): ThemeColors {
  const cs = getComputedStyle(doc.documentElement);
  const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
  return {
    bg: v('--color-bg', THEME_FALLBACKS.bg),
    surface: v('--color-surface', THEME_FALLBACKS.surface),
    surface2: v('--color-surface-2', THEME_FALLBACKS.surface2),
    border: v('--color-border', THEME_FALLBACKS.border),
    text: v('--color-text', THEME_FALLBACKS.text),
    textDim: v('--color-text-dim', THEME_FALLBACKS.textDim),
    gold: v('--color-gold', THEME_FALLBACKS.gold),
    goldBright: v('--color-gold-bright', THEME_FALLBACKS.goldBright),
    crimson: v('--color-crimson', THEME_FALLBACKS.crimson),
    purple: v('--color-purple', THEME_FALLBACKS.purple),
    purpleLight: v('--color-purple-light', THEME_FALLBACKS.purpleLight),
    error: v('--color-error', THEME_FALLBACKS.error),
    success: v('--color-success', THEME_FALLBACKS.success),
  };
}

export interface CellVisual {
  fill: string;
  stroke: string;
  lineWidth: number;
  /** Extra bright inset ring — used for resource nodes the caller has claimed, and for a colony's Bastion Wall tier. */
  ring?: string;
  /** Ring stroke width in canvas px. Defaults to 1.5 (drawCell) when unset — only Bastion Wall tiering varies this today. */
  ringLineWidth?: number;
}

/**
 * Bastion Wall level -> map-marker ring treatment. A photographic wall render
 * is unreadable at per-cell diamond scale (a handful of px), so the map
 * signals fortification tier through the SAME ring channel already used for
 * "resource node claimed by me" — a distinct color from the gold/crimson
 * ownership fill so it never reads as an ownership signal — thickening at
 * each tier rather than switching hue. Absent/0 draws no ring at all (the
 * pre-existing, unfortified look).
 */
function wallRingFor(wallLevel: number | null | undefined, colors: ThemeColors): { ring: string; ringLineWidth: number } | null {
  if (!wallLevel || wallLevel < 1) return null;
  // Spread wide (1.5 / 3 / 4.5, not a subtle +0.5 step) — at a ~20px diamond
  // marker, small stroke-width deltas round away against the DPR transform
  // and read as identical. A tripled-then-quadrupled width is what actually
  // survives down-scaling to "thicker ring", not just a different number.
  if (wallLevel < 3) return { ring: colors.purpleLight, ringLineWidth: 1.5 };
  if (wallLevel < 5) return { ring: colors.purpleLight, ringLineWidth: 3 };
  return { ring: colors.purpleLight, ringLineWidth: 4.5 };
}

/**
 * Static fill per decoration kind — deliberately NOT sourced from the UI
 * theme tokens (ThemeColors), same rationale as TERRAIN_BASE/TERRAIN_BLOBS
 * above: these are diegetic ground features, not chrome, and they never
 * change with ownership or claim state, so a fixed palette is correct here
 * (unlike wallRingFor/resolveCellVisual's other branches, which must read
 * live theme tokens because they render on the dark UI surface elsewhere).
 */
const DECORATION_COLORS: Record<string, { fill: string; stroke: string }> = {
  // Loading-placeholder only — every DECORATION cell has real art (CRATER's
  // own 1x1 crater-1/crater-2.webp for every scattered cell now, same as the
  // hand-placed MOUNTAIN/DUNE features) and falls through to this palette
  // only for the brief window before that art has loaded.
  MOUNTAIN: { fill: '#6b6259', stroke: '#4a443d' },
  CRATER: { fill: '#2e1f14', stroke: '#1a110b' },
  DUNE: { fill: '#c07a35', stroke: '#8a5522' },
};

/**
 * Frontend copy of the backend's WorldMapService.DUNE_LOCATIONS — same "no
 * shared types package" duplication precedent as GRID_WIDTH/SECTOR_COLS
 * above. Each anchor is the TOP-LEFT cell of a 2-wide x 3-tall footprint the
 * real dune sprite is warped across (see drawWarpedMultiCellSprite and
 * worldmap-screen.ts's dedicated dune draw pass). Keep in sync with the
 * backend list if either changes.
 */
export const DUNE_LOCATIONS: { x: number; y: number }[] = [
  { x: 18, y: 6 },
  { x: 62, y: 9 },
  { x: 40, y: 18 },
  { x: 12, y: 28 },
  { x: 68, y: 25 },
];

export const DUNE_WIDTH_CELLS = 2;
export const DUNE_HEIGHT_CELLS = 3;

/**
 * Frontend copy of the backend's WorldMapService.CRATER_LOCATIONS — same
 * duplication precedent as DUNE_LOCATIONS, but each entry carries its own
 * width/height since this feature mixes 2x2 and 1x1 footprints. Reuses the
 * existing CRATER DecorationKind — the per-cell formula's scattered 1x1
 * craters are untouched; these are additional, hand-placed, real-art
 * instances, distinguished at render time by appearing in this list. Keep
 * in sync with the backend list if either changes.
 */
export const CRATER_LOCATIONS: { x: number; y: number; width: number; height: number }[] = [
  { x: 8, y: 15, width: 2, height: 2 },
  { x: 55, y: 5, width: 2, height: 2 },
  { x: 30, y: 32, width: 2, height: 2 },
  { x: 70, y: 12, width: 2, height: 2 },
  { x: 50, y: 25, width: 1, height: 1 },
  { x: 25, y: 8, width: 1, height: 1 },
];

/**
 * Which CRATER_LOCATIONS entry (by index) a cell belongs to, or null if it
 * isn't part of any hand-placed crater — including an ordinary scattered,
 * per-cell-formula CRATER cell, which still renders the plain flat diamond
 * (resolveCellVisual's existing CRATER palette entry), unchanged.
 */
export function craterLocationIndexFor(x: number, y: number): number | null {
  for (let i = 0; i < CRATER_LOCATIONS.length; i++) {
    const loc = CRATER_LOCATIONS[i];
    if (x >= loc.x && x < loc.x + loc.width && y >= loc.y && y < loc.y + loc.height) return i;
  }
  return null;
}

/**
 * Which of the 2 crater art variants a SCATTERED (not hand-placed) 1x1
 * CRATER cell uses — every scattered CRATER cell now gets real art (see
 * worldmap-screen.ts's per-POI crater draw pass), not just the 6 hand-placed
 * CRATER_LOCATIONS instances. Deterministic from the cell's own coordinates
 * (never Math.random()) since there's no fixed location-list index to key
 * off for a scattered cell the way hand-placed features use.
 */
export function scatteredCraterArtVariantFor(x: number, y: number): number {
  return (x + y) % 2;
}

/**
 * Frontend copy of the backend's WorldMapService.MOUNTAIN_LOCATIONS — same
 * duplication precedent as CRATER_LOCATIONS. 4 art variants, 3 instances
 * each, in a fixed order (see MOUNTAIN_ART_VARIANT below for the index ->
 * image mapping). Keep in sync with the backend list if either changes.
 */
export const MOUNTAIN_LOCATIONS: { x: number; y: number; width: number; height: number }[] = [
  { x: 5, y: 10, width: 1, height: 2 },
  { x: 45, y: 32, width: 1, height: 2 },
  { x: 75, y: 18, width: 1, height: 2 },
  { x: 35, y: 5, width: 2, height: 1 },
  { x: 74, y: 28, width: 2, height: 1 },
  { x: 20, y: 30, width: 2, height: 1 },
  { x: 15, y: 22, width: 2, height: 2 },
  { x: 58, y: 33, width: 2, height: 2 },
  { x: 35, y: 12, width: 2, height: 2 },
  { x: 45, y: 4, width: 2, height: 2 },
  { x: 10, y: 35, width: 2, height: 2 },
  { x: 65, y: 20, width: 2, height: 2 },
];

/** Which of the 4 mountain art variants a MOUNTAIN_LOCATIONS entry (by index) uses — 3 consecutive entries per variant, in list order. */
export function mountainArtVariantFor(locationIndex: number): number {
  return Math.floor(locationIndex / 3);
}

/**
 * Which MOUNTAIN_LOCATIONS entry (by index) a cell belongs to, or null if it
 * isn't part of any hand-placed mountain — including an ordinary scattered,
 * per-cell-formula MOUNTAIN cell, which still renders the plain flat diamond
 * (resolveCellVisual's existing MOUNTAIN palette entry), unchanged.
 */
export function mountainLocationIndexFor(x: number, y: number): number | null {
  for (let i = 0; i < MOUNTAIN_LOCATIONS.length; i++) {
    const loc = MOUNTAIN_LOCATIONS[i];
    if (x >= loc.x && x < loc.x + loc.width && y >= loc.y && y < loc.y + loc.height) return i;
  }
  return null;
}

/**
 * World-map settlement artwork for a REAL PLAYER colony, tiered by the
 * colony's Command Center ("HQ") level — the same minLevel-list idiom as
 * engine/building-art.ts's BUILDING_ART_TIERS, and resolved the same way
 * (highest matching minLevel wins, so the list stays sorted descending and a
 * third stage is one more entry).
 *
 * Barbarian/bot camps are NOT drawn from this list — they keep their own
 * once-assigned `barbarianFortArtId` fort art, which takes precedence (see
 * worldmap-screen.ts's draw loop). `cellRatio` is per-tier rather than one
 * shared constant because the two renders are framed differently (a fuller
 * compound vs. one ringed by outlying bollards), so a single width ratio
 * would make the larger settlement read as accidentally off-scale instead of
 * deliberately bigger.
 *
 * Both ratios are kept well under 1 (the largest tier at 0.6, not the full
 * cell width) — colonies routinely sit in adjacent cells (bot clusters
 * especially), and the closest two plots ever get on screen is exactly one
 * tile width apart, so a ratio approaching 1 reads as neighboring sprites
 * touching or overlapping. 0.6 leaves a comfortable gap even between two
 * largest-tier neighbors.
 */
export interface ColonyMapArtTier {
  /** Command Center level (inclusive) at which this tier's art becomes current. */
  minLevel: number;
  src: string;
  /** On-screen width as a multiple of the plot's own cell width. */
  cellRatio: number;
}

/**
 * Source files are 768px wide, one per tier — no -1k/-2k siblings, since
 * nothing would consume them. 768 is sized from the worst case the zoom
 * ceiling can actually ask for, which is a WIDE DESKTOP, not mobile: a
 * narrow viewport raises `effectiveMaxZoom` (worldmap-screen.ts's
 * MIN_TAP_TARGET_PX) in inverse proportion to its width, so the near-edge
 * cell pins at ~98 CSS px there, while a 2560px viewport sits at the
 * MAX_ZOOM floor and reaches ~336 CSS px — 672 device px at DPR 2.
 */
export const COLONY_MAP_ART_TIERS: ColonyMapArtTier[] = [
  { minLevel: 6, src: '/colony/colony-hq6.webp', cellRatio: 0.6 },
  { minLevel: 1, src: '/colony/colony-hq1.webp', cellRatio: 0.48 },
];

/**
 * The settlement render for a player colony at `hqLevel`. Falls back to the
 * lowest tier for a null/absent/0 level rather than returning null: the plot
 * IS colonized (the caller only asks for colonized plots), and a colony whose
 * Command Center row has not been created yet — or whose level came back on
 * an older cached response without the field — must still look like a
 * settlement, not vanish from the map.
 */
export function colonyMapArtFor(hqLevel: number | null | undefined): ColonyMapArtTier {
  const level = hqLevel ?? 0;
  return COLONY_MAP_ART_TIERS.find((tier) => level >= tier.minLevel) ?? COLONY_MAP_ART_TIERS[COLONY_MAP_ART_TIERS.length - 1];
}

/**
 * Pure mapping from POI + ownership context to the colors it should be drawn
 * with, or `null` when nothing should be drawn at all. RailNation-style map
 * convention: the grid is an invisible positioning structure, not a visible
 * overlay — empty/unclaimed land shows only the terrain backdrop, with no
 * marker, fill, or border. Only POIs of actual interest (a claimed colony
 * plot, a resource node, a decoration) get a drawn marker.
 */
export function resolveCellVisual(
  poi: WorldMapPoi,
  myColonyId: string | null,
  colors: ThemeColors,
): CellVisual | null {
  if (poi.kind === 'DECORATION') {
    const palette = DECORATION_COLORS[poi.decorationKind ?? ''] ?? DECORATION_COLORS.MOUNTAIN;
    return { fill: palette.fill, stroke: palette.stroke, lineWidth: 1 };
  }
  if (poi.kind === 'RESOURCE_NODE') {
    // No "claimed by me" ring any more — caves cannot be held, only raided,
    // so every one of them looks the same to every player.
    return { fill: colors.purple, stroke: colors.purpleLight, lineWidth: 1.5 };
  }
  // COLONY_PLOT — unclaimed land renders nothing at all (invisible grid).
  if (!poi.colonyId) {
    return null;
  }
  const wallRing = wallRingFor(poi.wallLevel, colors);
  if (poi.colonyId === myColonyId) {
    return { fill: colors.gold, stroke: colors.goldBright, lineWidth: 2, ...wallRing };
  }
  return { fill: colors.crimson, stroke: colors.crimson, lineWidth: 1, ...wallRing };
}

/** A colony-name map label never shows more than this many characters — longer names are cut with a trailing ellipsis. */
export const COLONY_LABEL_MAX_CHARS = 14;

/** Truncates a colony name for map-label display, never exceeding `maxChars` — the cut is marked with a trailing ellipsis so it never reads as the whole name. */
export function truncateColonyLabel(name: string, maxChars: number = COLONY_LABEL_MAX_CHARS): string {
  if (name.length <= maxChars) return name;
  return `${name.slice(0, maxChars - 1)}…`;
}

/**
 * A colony label's font size scales with its own marker's on-screen
 * half-width — the SAME value the caller just used to size the diamond or
 * barbarian-fort icon above it — so the name grows and shrinks together
 * with the icon across zoom levels and map rows (a far row's smaller
 * marker gets a smaller label; a near row's larger marker gets a larger
 * one), clamped so it never drops below legible or balloons past its
 * neighbors.
 */
export const COLONY_LABEL_MIN_FONT_PX = 8;
export const COLONY_LABEL_MAX_FONT_PX = 15;
export const COLONY_LABEL_FONT_RATIO = 0.55;

export function colonyLabelFontSizePx(markerHalfWidthPx: number): number {
  return Math.min(COLONY_LABEL_MAX_FONT_PX, Math.max(COLONY_LABEL_MIN_FONT_PX, markerHalfWidthPx * COLONY_LABEL_FONT_RATIO));
}

/**
 * Draws a colony's (truncated) name centered at (centerX, labelY) — callers
 * pass labelY just below whatever marker/icon they already drew. A dark
 * stroke halo behind the fill keeps it legible over the photographic Mars
 * background regardless of the terrain color underneath.
 */
export function drawColonyLabel(
  ctx: CanvasRenderingContext2D,
  name: string,
  centerX: number,
  labelY: number,
  markerHalfWidthPx: number,
  color: string,
): void {
  const fontSizePx = colonyLabelFontSizePx(markerHalfWidthPx);
  const label = truncateColonyLabel(name);
  ctx.save();
  ctx.font = `${fontSizePx}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.lineWidth = Math.max(1, fontSizePx * 0.2);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.strokeText(label, centerX, labelY);
  ctx.fillStyle = color;
  ctx.fillText(label, centerX, labelY);
  ctx.restore();
}

/**
 * Draws one cell marker (a POI) as a diamond centered at (centerX, centerY),
 * sized by explicit half-width/half-height in screen pixels — the caller
 * derives these from that cell's actual on-screen footprint, which varies by
 * row under the trapezoid projection (see worldmap-screen.ts's toScreen()).
 * Stroke widths are left in constant screen px (not scaled), matching how
 * POI markers read at any zoom level.
 */
export function drawCell(
  ctx: CanvasRenderingContext2D,
  visual: CellVisual,
  centerX: number,
  centerY: number,
  halfWidth: number,
  halfHeight: number,
): void {
  const padX = Math.max(1, halfWidth * 0.08);
  const padY = Math.max(1, halfHeight * 0.08);
  const innerHw = Math.max(0, halfWidth - padX);
  const innerHh = Math.max(0, halfHeight - padY);

  const diamondPath = (dx: number, dy: number): void => {
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - dy);
    ctx.lineTo(centerX + dx, centerY);
    ctx.lineTo(centerX, centerY + dy);
    ctx.lineTo(centerX - dx, centerY);
    ctx.closePath();
  };

  if (visual.fill) {
    ctx.fillStyle = visual.fill;
    diamondPath(innerHw, innerHh);
    ctx.fill();
  }
  ctx.strokeStyle = visual.stroke;
  ctx.lineWidth = visual.lineWidth;
  diamondPath(innerHw, innerHh);
  ctx.stroke();
  if (visual.ring) {
    const ringPadX = Math.max(0, padX - 3);
    const ringPadY = Math.max(0, padY - 3);
    ctx.strokeStyle = visual.ring;
    ctx.lineWidth = visual.ringLineWidth ?? 1.5;
    diamondPath(halfWidth - ringPadX, halfHeight - ringPadY);
    ctx.stroke();
  }
}

/**
 * Draws the full grid as a connected mesh of straight lines under the
 * trapezoid projection — `gridWidth + 1` column lines (each dead straight:
 * xu is affine in t for a fixed column index, so far-edge and near-edge
 * endpoints alone fully describe it) and `gridHeight + 1` row lines (each a
 * horizontal segment, since yu is linear and doesn't shear x). One
 * fill-free `CellVisual`-shaped stroke pass, drawn as a single path so it's
 * one `stroke()` call rather than 3200 separate ones. This replaces
 * per-cell diamond markers for empty land — diamonds inscribed in an
 * axis-aligned grid never tile edge-to-edge, so drawing one per cell reads
 * as scattered shapes rather than a connected surface.
 */
export function drawGridLines(
  ctx: CanvasRenderingContext2D,
  strokeColor: string,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  farWidthRatio: number = FAR_EDGE_WIDTH_RATIO,
): void {
  const toScreen = (xu: number, yu: number): { x: number; y: number } => ({
    x: xu * displayWidth + panOffset.x + displayWidth / 2,
    y: yu * displayHeight + panOffset.y,
  });

  ctx.save();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 1;
  ctx.beginPath();

  for (let k = 0; k <= gridWidth; k++) {
    const colFraction = k / gridWidth - 0.5;
    const far = toScreen(colFraction * rowWidthRatio(0, farWidthRatio), 0);
    const near = toScreen(colFraction * rowWidthRatio(1, farWidthRatio), 1);
    ctx.moveTo(far.x, far.y);
    ctx.lineTo(near.x, near.y);
  }

  for (let j = 0; j <= gridHeight; j++) {
    const t = j / gridHeight;
    const halfWidth = 0.5 * rowWidthRatio(t, farWidthRatio);
    const left = toScreen(-halfWidth, t);
    const right = toScreen(halfWidth, t);
    ctx.moveTo(left.x, left.y);
    ctx.lineTo(right.x, right.y);
  }

  ctx.stroke();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Sheared isometric grid projection (world map only — colony-city-renderer.ts's
// 5x5 build grid keeps using the trapezoid functions above unchanged)
// ---------------------------------------------------------------------------

/**
 * Overall map height as a fraction of its width. Unlike the trapezoid
 * projection above, NOTHING here depends on row position — every cell is an
 * identical parallelogram regardless of where it sits on the grid, which is
 * the whole point: "all cells are the same" rather than tapering with
 * simulated depth.
 */
export const ISO_HEIGHT_TO_WIDTH_RATIO = 0.5;

/**
 * Total sideways shift (as a fraction of one row's own width) from the top
 * row to the bottom row — the "half a tile width per row" classic oblique
 * shear angle, derived from the grid's aspect ratio rather than picked as a
 * magic number: each full row-step shifts x by half a tile width
 * (1/(2*gridWidth) of a row's own width), accumulated over gridHeight rows.
 */
function shearFraction(gridWidth: number, gridHeight: number): number {
  return gridHeight / (2 * gridWidth);
}

/**
 * Grid cell -> unit-space position. Deliberately the SAME [-0.5, 0.5] x
 * [0, 1] unit-space convention the trapezoid functions above use (x centered
 * on 0, y starting at 0), so callers (toScreen, pan clamping, zoom math)
 * don't need two different conventions — only the formula producing (x, y)
 * differs.
 *
 * y depends ONLY on gy (a flat row — every cell in the same row shares the
 * same y, contrast gridToProjected's trapezoid, and contrast an earlier
 * version of this function where y mixed in gx too, rotating the whole grid
 * into a single pointed diamond corner at the top instead of a flat edge).
 * x is gx's own column position PLUS a shear term that grows with gy, so
 * each row is shifted sideways relative to the row above it — moving one
 * step in gx shifts x by a constant (one tile width); moving one step in gy
 * shifts x by a constant HALF-tile-width shear (see shearFraction) and y by
 * one tile height. No term depends on the OTHER coordinate's own value
 * (only on which coordinate moved), which is what makes every tile
 * identical regardless of position.
 *
 * The raw (pre-normalization) x span is 1 row-width PLUS the total shear —
 * wider than the single-row convention trapezoid uses — so the whole
 * expression is divided by (1 + shear) to renormalize the overall bounding
 * parallelogram back to exactly [-0.5, 0.5], same as every caller expects.
 */
export function isoGridToProjected(
  gx: number,
  gy: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): ProjectedPoint {
  const shear = shearFraction(gridWidth, gridHeight);
  const xRaw = (gx / gridWidth - 0.5) + shear * (gy / gridHeight - 0.5);
  return { x: xRaw / (1 + shear), y: gy / gridHeight };
}

/** Inverse of isoGridToProjected, unrounded — the fractional grid position under a given unit-space point. */
export function isoProjectedToGridContinuous(
  xu: number,
  yu: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): ProjectedPoint {
  const shear = shearFraction(gridWidth, gridHeight);
  const gy = yu * gridHeight;
  const xRaw = xu * (1 + shear);
  const gx = gridWidth * (xRaw - shear * (yu - 0.5) + 0.5);
  return { x: gx, y: gy };
}

/**
 * Unit-space point -> the grid cell containing it, or null outside the grid.
 * Same floor-not-round reasoning as projectedToGridCell above (integer grid
 * coordinates are cell edges, not centers) — the iso transform is still
 * linear, so that convention carries over unchanged.
 */
export function isoProjectedToGridCell(
  xu: number,
  yu: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): GridCell | null {
  const exact = isoProjectedToGridContinuous(xu, yu, gridWidth, gridHeight);
  const gx = Math.floor(exact.x) || 0;
  const gy = Math.floor(exact.y) || 0;
  if (gx < 0 || gx >= gridWidth || gy < 0 || gy >= gridHeight) return null;
  return { x: gx, y: gy };
}

/** Grid cell -> screen pixel, folding in the current pan offset and display size the same way worldmap-screen.ts's own (now-removed) local toScreen used to. Exported so every iso drawing helper below shares one implementation instead of each re-deriving it. */
export function isoToScreen(
  gx: number,
  gy: number,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): { x: number; y: number } {
  const p = isoGridToProjected(gx, gy, gridWidth, gridHeight);
  return { x: p.x * displayWidth + panOffset.x + displayWidth / 2, y: p.y * displayHeight + panOffset.y };
}

/**
 * One grid cell's own on-screen width/height in px — CONSTANT everywhere
 * under the iso projection (contrast the trapezoid's row-dependent
 * `rowWidthRatio(t) * displayWidth / GRID_WIDTH`), so callers can compute it
 * once per draw() call instead of once per POI. Height is simply
 * displayHeight/gridHeight (y never mixes in the shear); width accounts for
 * the same (1 + shear) renormalization isoGridToProjected applies.
 */
export function isoTileSizePx(
  displayWidth: number,
  displayHeight: number,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): { width: number; height: number } {
  const shear = shearFraction(gridWidth, gridHeight);
  return { width: displayWidth / (gridWidth * (1 + shear)), height: displayHeight / gridHeight };
}

export interface SectorBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * The world map is partitioned into a 6x4 (24) sector grid for spawn/
 * barbarian pacing — see backend/src/game/worldmap/worldmap.types.ts's
 * SECTOR_COLS/SECTOR_ROWS. No shared types package exists between frontend
 * and backend in this repo (GRID_WIDTH/GRID_HEIGHT/PoiKind are already
 * independently duplicated the same way), so these are a frontend-side copy
 * for the sector overlay — pure arithmetic, no network round-trip needed.
 * Keep in sync with the backend constants/function if either changes.
 */
export const SECTOR_COLS = 6;
export const SECTOR_ROWS = 4;

/** Frontend copy of worldmap.types.ts's sectorsFor — see SECTOR_COLS doc above. */
export function sectorsFor(width: number, height: number, cols: number = SECTOR_COLS, rows: number = SECTOR_ROWS): SectorBounds[] {
  const colEdge = (i: number) => Math.floor((i * width) / cols);
  const rowEdge = (j: number) => Math.floor((j * height) / rows);
  const sectors: SectorBounds[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      sectors.push({ minX: colEdge(col), maxX: colEdge(col + 1) - 1, minY: rowEdge(row), maxY: rowEdge(row + 1) - 1 });
    }
  }
  return sectors;
}

/** Frontend copy of worldmap.types.ts's sectorUnlockOrder — square rings grown from the top-left, each new sector edge-adjacent to an open one. */
export function sectorUnlockOrder(cols: number = SECTOR_COLS, rows: number = SECTOR_ROWS): number[] {
  const order: number[] = [];
  for (let k = 0; k < Math.max(cols, rows); k++) {
    if (k < cols) {
      for (let row = 0; row <= Math.min(k, rows - 1); row++) order.push(row * cols + k);
    }
    if (k < rows) {
      for (let col = Math.min(k, cols) - 1; col >= 0; col--) order.push(k * cols + col);
    }
  }
  return order;
}

/** Same default as the backend's SECTOR_FILL_THRESHOLD. */
export const SECTOR_FILL_THRESHOLD = 0.2;

/**
 * Which sectors the fog should reveal: every sector in unlock order up to
 * and including the active one (the first still under the fill threshold —
 * the backend's activeSectorIndex), plus any sector that already holds a
 * claimed colony, so a colony settled under an older ordering is never
 * hidden in fog. A zero-total sector counts as full, like the backend.
 * When nothing is under threshold every sector is revealed.
 */
export function revealedSectors(
  counts: { claimed: number; total: number }[],
  cols: number = SECTOR_COLS,
  rows: number = SECTOR_ROWS,
  threshold: number = SECTOR_FILL_THRESHOLD,
): Set<number> {
  const revealed = new Set<number>();
  for (const i of sectorUnlockOrder(cols, rows)) {
    if (i >= counts.length) continue;
    revealed.add(i);
    const c = counts[i];
    if (c.total > 0 && c.claimed / c.total < threshold) break;
  }
  counts.forEach((c, i) => {
    if (c.claimed > 0) revealed.add(i);
  });
  return revealed;
}

/**
 * Draws the 6x4 sector grid as a heavier overlay on top of the fine
 * per-cell grid (drawGridLines) — a thicker stroke so sector seams read as
 * a coarser mesh layered over the cell mesh, not more of the same lines. A
 * sibling to drawGridLines rather than a parameter on it: drawGridLines is
 * also called by colony-city-renderer.ts for the unrelated 5x5 colony-city
 * grid, which has no sector concept at all — changing drawGridLines itself
 * would bleed sector lines into that screen.
 *
 * Each sector's 4 corners are computed directly via gridToProjected (not
 * interpolated) and connected with straight lines — both a fixed row (y
 * linear, doesn't shear x) and a fixed column (x affine in t) are already
 * exactly straight under this projection, same as drawGridLines' own column
 * lines, so 4 corners fully describe each sector's quad with no
 * approximation.
 *
 * A sector's bounds are cell INDICES (inclusive) — the far screen edge of
 * each box is the NEXT edge, `maxX + 1` / `maxY + 1`, not `maxX`/`maxY`
 * themselves (integer grid coordinates are cell edges, not centers — see
 * projectedToGridCell's doc comment). Using maxX/maxY directly would draw
 * every sector one cell short on its bottom/right side.
 */
export function drawSectorLines(
  ctx: CanvasRenderingContext2D,
  strokeColor: string,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  farWidthRatio: number = FAR_EDGE_WIDTH_RATIO,
  cols: number = SECTOR_COLS,
  rows: number = SECTOR_ROWS,
): void {
  const toScreen = (xu: number, yu: number): { x: number; y: number } => ({
    x: xu * displayWidth + panOffset.x + displayWidth / 2,
    y: yu * displayHeight + panOffset.y,
  });
  const gridPoint = (gx: number, gy: number): { x: number; y: number } => {
    const p = gridToProjected(gx, gy, gridWidth, gridHeight, farWidthRatio);
    return toScreen(p.x, p.y);
  };

  const sectors = sectorsFor(gridWidth, gridHeight, cols, rows);

  ctx.save();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 5;
  ctx.beginPath();

  for (const sector of sectors) {
    const topLeft = gridPoint(sector.minX, sector.minY);
    const topRight = gridPoint(sector.maxX + 1, sector.minY);
    const bottomRight = gridPoint(sector.maxX + 1, sector.maxY + 1);
    const bottomLeft = gridPoint(sector.minX, sector.maxY + 1);

    ctx.moveTo(topLeft.x, topLeft.y);
    ctx.lineTo(topRight.x, topRight.y);
    ctx.lineTo(bottomRight.x, bottomRight.y);
    ctx.lineTo(bottomLeft.x, bottomLeft.y);
    ctx.closePath();
  }

  ctx.stroke();
  ctx.restore();
}

/**
 * Iso equivalent of drawSectorLines — each sector's 4 corners via
 * isoToScreen, connected with straight lines. Simpler than the trapezoid
 * version's doc comment needed to justify: EVERY line is straight under an
 * affine transform (no row-dependent shear to reason about), so 4 corners
 * always exactly describe a sector's quad, for any grid shape.
 */
export function drawIsoSectorLines(
  ctx: CanvasRenderingContext2D,
  strokeColor: string,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  cols: number = SECTOR_COLS,
  rows: number = SECTOR_ROWS,
): void {
  const gridPoint = (gx: number, gy: number): { x: number; y: number } =>
    isoToScreen(gx, gy, displayWidth, displayHeight, panOffset, gridWidth, gridHeight);

  const sectors = sectorsFor(gridWidth, gridHeight, cols, rows);

  ctx.save();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 5;
  ctx.beginPath();

  for (const sector of sectors) {
    const topLeft = gridPoint(sector.minX, sector.minY);
    const topRight = gridPoint(sector.maxX + 1, sector.minY);
    const bottomRight = gridPoint(sector.maxX + 1, sector.maxY + 1);
    const bottomLeft = gridPoint(sector.minX, sector.maxY + 1);

    ctx.moveTo(topLeft.x, topLeft.y);
    ctx.lineTo(topRight.x, topRight.y);
    ctx.lineTo(bottomRight.x, bottomRight.y);
    ctx.lineTo(bottomLeft.x, bottomLeft.y);
    ctx.closePath();
  }

  ctx.stroke();
  ctx.restore();
}

export interface MultiCellSpriteBox {
  /** Top-left cell's grid x. */
  minX: number;
  /** Top-left cell's grid y. */
  minY: number;
  /** Footprint width, in cells. */
  width: number;
  /** Footprint height, in cells. */
  height: number;
}

export interface ContainFit {
  /** Fraction (0-1] of the reference box's width the image actually occupies once contain-fit. */
  widthFraction: number;
  /** Fraction (0-1] of the reference box's height the image actually occupies once contain-fit. */
  heightFraction: number;
}

/**
 * Given a reference box's pixel size and an image's own aspect ratio
 * (width/height), compute how much of the box's width/height a contain-fit
 * (scale to fit, preserve aspect, centered) placement of that image actually
 * occupies. Exactly one of the two returned fractions is 1 (the box's
 * limiting axis); the other is less than 1 (how much of THAT axis the image
 * fills once scaled to match the limiting one) — centering the image is then
 * just insetting by `(1 - fraction) / 2` on whichever axis isn't 1.
 *
 * Pure math, no canvas — kept separate from drawWarpedMultiCellSprite so it
 * can be unit-tested directly, consistent with this file's convention of
 * only testing pure geometry, never draw-only canvas functions.
 */
export function containFitFractions(refWidthPx: number, refHeightPx: number, imageAspect: number): ContainFit {
  if (refWidthPx <= 0 || refHeightPx <= 0 || imageAspect <= 0) return { widthFraction: 1, heightFraction: 1 };
  const fitWidthPx = refHeightPx * imageAspect;
  if (fitWidthPx <= refWidthPx) {
    return { widthFraction: fitWidthPx / refWidthPx, heightFraction: 1 };
  }
  return { widthFraction: 1, heightFraction: refWidthPx / fitWidthPx };
}

/**
 * Draws one image warped across an arbitrary W x H cell footprint, following
 * the grid's real trapezoid skew — a fixed column is dead straight (xu
 * affine in t) and a fixed row is horizontal (yu linear, no shear), same
 * facts drawGridLines/drawSectorLines already rely on, but canvas 2D still
 * can't texture-map a single image onto a non-rectangular quad in one call.
 * So, same technique drawBackgroundStrips already uses for the full-width
 * Mars background: slice into many thin horizontal sub-bands, each its own
 * tiny axis-aligned `drawImage`, with that band's own left edge/width
 * precisely interpolated from the true column-affine math (via
 * gridToProjected at the band's own fractional row position) rather than
 * reusing one cell's nominal center for the whole multi-row footprint — thin
 * enough bands make the per-band "steps" invisible, so the aggregate reads
 * as one continuous, correctly-tapering shape instead of disjointed boxes.
 *
 * Contain-fits the image inside the footprint's bounding box (via
 * containFitFractions, using the footprint's NEAR/bottom edge width and
 * total height as the reference rectangle) rather than stretching it to
 * exactly fill a non-matching aspect ratio — centered on whichever axis has
 * slack.
 */
export function drawWarpedMultiCellSprite(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  box: MultiCellSpriteBox,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
  farWidthRatio: number = FAR_EDGE_WIDTH_RATIO,
  bands = 24,
): void {
  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) return;

  const toScreen = (xu: number, yu: number): { x: number; y: number } => ({
    x: xu * displayWidth + panOffset.x + displayWidth / 2,
    y: yu * displayHeight + panOffset.y,
  });
  // Unit-space x of grid column `gx` at fractional row position `t` (0 = far
  // edge, 1 = near edge) — gridToProjected takes gy, not t directly, but
  // t = gy / gridHeight internally, so gy = t * gridHeight recovers exactly
  // that t for any fractional row position, not just integer cell edges.
  const colXu = (gx: number, t: number): number => gridToProjected(gx, t * gridHeight, gridWidth, gridHeight, farWidthRatio).x;

  const topT = box.minY / gridHeight;
  const bottomT = (box.minY + box.height) / gridHeight;

  const bottomLeftXu = colXu(box.minX, bottomT);
  const bottomRightXu = colXu(box.minX + box.width, bottomT);
  const refWidthPx = (bottomRightXu - bottomLeftXu) * displayWidth;
  const refHeightPx = (bottomT - topT) * displayHeight;
  if (refWidthPx <= 0 || refHeightPx <= 0) return;

  const { widthFraction, heightFraction } = containFitFractions(refWidthPx, refHeightPx, image.naturalWidth / image.naturalHeight);
  const insetT0 = topT + ((bottomT - topT) * (1 - heightFraction)) / 2;
  const insetT1 = bottomT - ((bottomT - topT) * (1 - heightFraction)) / 2;

  for (let i = 0; i < bands; i++) {
    const tA = insetT0 + (insetT1 - insetT0) * (i / bands);
    const tB = insetT0 + (insetT1 - insetT0) * ((i + 1) / bands);
    const tMid = (tA + tB) / 2;

    const leftXuFull = colXu(box.minX, tMid);
    const rightXuFull = colXu(box.minX + box.width, tMid);
    const centerXu = (leftXuFull + rightXuFull) / 2;
    const halfWidthXu = ((rightXuFull - leftXuFull) * widthFraction) / 2;

    const left = toScreen(centerXu - halfWidthXu, tMid);
    const right = toScreen(centerXu + halfWidthXu, tMid);
    const topY = toScreen(0, tA).y;
    const bottomY = toScreen(0, tB).y;

    const destX = left.x;
    const destWidth = right.x - left.x;
    const destHeight = bottomY - topY + 1; // +1 overlap, same hairline-seam fix drawBackgroundStrips uses

    const sy = image.naturalHeight * (i / bands);
    const sHeight = image.naturalHeight / bands;

    ctx.drawImage(image, 0, sy, image.naturalWidth, sHeight, destX, topY, destWidth, destHeight);
  }
}

/**
 * Iso equivalent of drawWarpedMultiCellSprite — but since the isometric
 * transform is affine (no perspective/depth distortion, unlike the
 * trapezoid's row-dependent taper), a rectangular W x H footprint always
 * maps to an exact PARALLELOGRAM in screen space, and canvas 2D can
 * texture-map an image onto a parallelogram directly via `ctx.transform` in
 * ONE drawImage call — no more thin-sub-band slicing hack needed at all.
 *
 * `ctx.transform` (not `setTransform`) composes with whatever transform is
 * already active (the canvas's own DPR scale, applied once in resize() and
 * left on the stack across the whole draw() pass) rather than replacing it,
 * so this works correctly regardless of device pixel ratio without needing
 * to know or re-derive it here.
 */
export function drawIsoMultiCellSprite(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  box: MultiCellSpriteBox,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): void {
  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) return;

  const screenAt = (gx: number, gy: number): { x: number; y: number } =>
    isoToScreen(gx, gy, displayWidth, displayHeight, panOffset, gridWidth, gridHeight);

  const topLeft = screenAt(box.minX, box.minY);
  const topRight = screenAt(box.minX + box.width, box.minY);
  const bottomLeft = screenAt(box.minX, box.minY + box.height);
  // bottomRight is never needed explicitly — it's implied by affine
  // linearity (topLeft + uVec + vVec below), same reasoning that makes 3
  // points (origin + 2 basis vectors) enough to fully describe an affine map.
  const uVec = { x: topRight.x - topLeft.x, y: topRight.y - topLeft.y };
  const vVec = { x: bottomLeft.x - topLeft.x, y: bottomLeft.y - topLeft.y };
  const refWidthPx = Math.hypot(uVec.x, uVec.y);
  const refHeightPx = Math.hypot(vVec.x, vVec.y);
  if (refWidthPx <= 0 || refHeightPx <= 0) return;

  const { widthFraction, heightFraction } = containFitFractions(refWidthPx, refHeightPx, image.naturalWidth / image.naturalHeight);
  const insetU = (1 - widthFraction) / 2;
  const insetV = (1 - heightFraction) / 2;
  const originX = topLeft.x + uVec.x * insetU + vVec.x * insetV;
  const originY = topLeft.y + uVec.y * insetU + vVec.y * insetV;
  const scaledUVec = { x: uVec.x * widthFraction, y: uVec.y * widthFraction };
  const scaledVVec = { x: vVec.x * heightFraction, y: vVec.y * heightFraction };

  ctx.save();
  ctx.transform(
    scaledUVec.x / image.naturalWidth,
    scaledUVec.y / image.naturalWidth,
    scaledVVec.x / image.naturalHeight,
    scaledVVec.y / image.naturalHeight,
    originX,
    originY,
  );
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}

/**
 * Draws one image UPRIGHT (never warped to the footprint's parallelogram
 * shape, unlike drawIsoMultiCellSprite above) centered on an arbitrary W x H
 * cell footprint — contain-fit against that footprint's own on-screen
 * width/height (via isoTileSizePx), same convention drawIsoCellSprite already
 * uses for single-cell decorations, just sized to a multi-cell box instead of
 * one tile.
 */
export function drawIsoMultiCellSpriteUpright(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  box: MultiCellSpriteBox,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): void {
  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) return;
  const center = isoToScreen(box.minX + box.width / 2, box.minY + box.height / 2, displayWidth, displayHeight, panOffset, gridWidth, gridHeight);
  const tile = isoTileSizePx(displayWidth, displayHeight, gridWidth, gridHeight);
  const refW = tile.width * box.width;
  const refH = tile.height * box.height;
  if (refW <= 0 || refH <= 0) return;
  const { widthFraction, heightFraction } = containFitFractions(refW, refH, image.naturalWidth / image.naturalHeight);
  const drawW = refW * widthFraction;
  const drawH = refH * heightFraction;
  ctx.drawImage(image, center.x - drawW / 2, center.y - drawH / 2, drawW, drawH);
}

/**
 * Deterministic pseudo-random in [0, 1), same technique as pseudoRandom
 * below (shader-style sine hash) — never Math.random(), so a cell's jitter
 * is stable across redraws/sessions. A distinct `salt` lets two different
 * decoration kinds (e.g. scattered craters vs. Crimson Dust) land on
 * different jitter values even where their (x, y) coincidentally match —
 * they never actually occupy the same cell, but keeping the hash input
 * distinguishable is cheap and avoids relying on that invariant.
 */
function cellJitterRandom(x: number, y: number, salt: number): number {
  return pseudoRandom(x * 374761 + y * 668265 + salt * 2654435761);
}

/** How much smaller than a full tile a scattered single-cell decoration (crater, Crimson Dust) renders — leaves headroom so CELL_SPRITE_JITTER_FRACTION's offset never pushes the sprite past the tile's own edge into a neighbor. */
export const CELL_SPRITE_SCALE = 0.62;
/** Max jitter offset applied to a scattered single-cell sprite's center, as a fraction of one tile's width/height — breaks the "every sprite sits dead-center" look that would otherwise make the underlying grid visible as a lattice, without the random look it wiggling under rendering changes. */
export const CELL_SPRITE_JITTER_FRACTION = 0.22;

/**
 * Draws one image at a SCALED-DOWN, JITTERED position within its own cell —
 * for decorations meant to read as loose scattered objects (a crater, a
 * Crimson Dust outcrop) rather than a tile-filling texture. Deliberately a
 * plain upright `drawImage` (not warped to the tile's own parallelogram
 * shape, unlike drawIsoMultiCellSprite) — the same convention settlement/fort icons
 * already use elsewhere on this map, appropriate here too since these are
 * now sub-tile objects sitting ON the ground, not ground itself.
 */
export function drawIsoCellSprite(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  gx: number,
  gy: number,
  jitterSalt: number,
  displayWidth: number,
  displayHeight: number,
  panOffset: PanOffset,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): void {
  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) return;

  const jitterX = (cellJitterRandom(gx, gy, jitterSalt) - 0.5) * 2 * CELL_SPRITE_JITTER_FRACTION;
  const jitterY = (cellJitterRandom(gx, gy, jitterSalt + 1) - 0.5) * 2 * CELL_SPRITE_JITTER_FRACTION;
  const center = isoToScreen(gx + 0.5 + jitterX, gy + 0.5 + jitterY, displayWidth, displayHeight, panOffset, gridWidth, gridHeight);

  const tile = isoTileSizePx(displayWidth, displayHeight, gridWidth, gridHeight);
  const boxWidthPx = tile.width * CELL_SPRITE_SCALE;
  const boxHeightPx = tile.height * CELL_SPRITE_SCALE;
  const { widthFraction, heightFraction } = containFitFractions(boxWidthPx, boxHeightPx, image.naturalWidth / image.naturalHeight);
  const drawWidth = boxWidthPx * widthFraction;
  const drawHeight = boxHeightPx * heightFraction;

  ctx.drawImage(image, center.x - drawWidth / 2, center.y - drawHeight / 2, drawWidth, drawHeight);
}

/**
 * Terrain palette is intentionally NOT sourced from the app's dark UI theme
 * tokens (styles/app.css) — those describe chrome (dark purple/gold), not the
 * diegetic Mars ground, and blending them over the near-black `bg` token
 * would read as a night sky rather than rust/ochre terrain. These are local,
 * documented constants for the canvas-painted terrain backdrop only. Cell
 * markers drawn on top of the terrain (colony plots, resource nodes) DO use
 * theme tokens via readThemeColors() above.
 *
 * Only used as a fallback while the real Mars image (mars-texture.ts) hasn't
 * loaded yet, or if it fails to load.
 */
const TERRAIN_BASE = '#7a4a2b';
const TERRAIN_BLOBS: { xRatio: number; yRatio: number; radiusRatio: number; color: string }[] = [
  { xRatio: 0.18, yRatio: 0.22, radiusRatio: 0.55, color: 'rgba(196, 120, 66, 0.35)' },
  { xRatio: 0.82, yRatio: 0.15, radiusRatio: 0.48, color: 'rgba(150, 82, 45, 0.30)' },
  { xRatio: 0.5, yRatio: 0.55, radiusRatio: 0.65, color: 'rgba(214, 140, 80, 0.25)' },
  { xRatio: 0.85, yRatio: 0.82, radiusRatio: 0.5, color: 'rgba(120, 60, 32, 0.35)' },
  { xRatio: 0.12, yRatio: 0.85, radiusRatio: 0.42, color: 'rgba(180, 110, 60, 0.28)' },
];
const TERRAIN_NOISE_SHADES = ['rgba(40, 20, 10, 0.05)', 'rgba(255, 200, 150, 0.05)', 'rgba(120, 70, 30, 0.04)'];

/** Deterministic pseudo-random in [0, 1) — keeps the per-cell terrain noise stable across cache rebuilds. */
function pseudoRandom(seed: number): number {
  const s = Math.sin(seed * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Paints a Mars-surface backdrop into `ctx`: a base rust tone, a handful of
 * large soft radial gradients to break up the flatness, and a subtle noise
 * pass, filling a `width` x `height` rectangle starting at (0, 0) in the
 * target context's own space (caller translates first if it needs to be
 * positioned elsewhere).
 */
export function drawTerrainBackground(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  noiseCellsX = 40,
  noiseCellsY = 20,
): void {
  ctx.save();
  ctx.fillStyle = TERRAIN_BASE;
  ctx.fillRect(0, 0, width, height);

  for (const blob of TERRAIN_BLOBS) {
    const cx = blob.xRatio * width;
    const cy = blob.yRatio * height;
    const r = blob.radiusRatio * Math.max(width, height);
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    gradient.addColorStop(0, blob.color);
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  const stepX = width / noiseCellsX;
  const stepY = height / noiseCellsY;
  for (let gy = 0; gy < noiseCellsY; gy++) {
    for (let gx = 0; gx < noiseCellsX; gx++) {
      const seed = gx * 928371 + gy * 132871 + 17;
      const shade = TERRAIN_NOISE_SHADES[Math.floor(pseudoRandom(seed) * TERRAIN_NOISE_SHADES.length)];
      ctx.fillStyle = shade;
      ctx.fillRect(gx * stepX, gy * stepY, stepX, stepY);
    }
  }
  ctx.restore();
}
